import { useCallback, useEffect, useRef, useState } from 'react'
import { sma, ema } from '@/indicators/ema_sma_quantum/engine'
import { buildWatches, evaluateBars, formatEventMessage, maLabel } from '@/indicators/ema_sma_quantum/notifications'
import { MAX_NOTIFICATION_EVENTS } from '@/indicators/ema_sma_quantum/constants'

let _eventIdCounter = 0
const nextEventId = () => `emasma-evt-${Date.now()}-${++_eventIdCounter}`

// Recalcula las series de las medias ACTIVAS sobre el set de velas completo
// (igual que detect.js, para que los valores sean idénticos a los pintados)
// y arma "barras cerradas" — la última posición de `candles` es siempre la
// vela en formación (ver useChartData.js: se actualiza in-place hasta que
// currentCandleTime > last.time), así que se excluye SIEMPRE.
function buildClosedBars(candles, mas) {
  const closedCount = candles.length - 1
  if (closedCount <= 0) return []
  const activeMas = (mas || []).filter(m => m.active)
  const seriesByMaId = {}
  for (const ma of activeMas) {
    seriesByMaId[ma.id] = ma.type === 'sma' ? sma(candles, ma.period) : ema(candles, ma.period)
  }
  const bars = new Array(closedCount)
  for (let i = 0; i < closedCount; i++) {
    const values = {}
    for (const ma of activeMas) {
      const v = seriesByMaId[ma.id][i]
      values[ma.id] = Number.isFinite(v) ? v : null
    }
    bars[i] = { time: candles[i].time, price: candles[i].close, values }
  }
  return bars
}

// Hook de wiring React para las notificaciones de ema_sma_quantum. La lógica
// de detección en sí vive en indicators/ema_sma_quantum/notifications.js
// (pura, portable) — este hook sólo:
//   1. Decide CUÁNDO evaluar (gateado por `enabled`, con reset de cursor al
//      activar o al cambiar de símbolo/TF — nunca retroactivo).
//   2. Persiste vía el objeto `notifications` de vuelta en la config del
//      indicador (el llamador debe hacer setConfig con lo que devuelve
//      computeNotificationUpdate si no es null — la propia persistencia
//      remota/local ya la gestiona useIndicatorConfig, no hay nada nuevo
//      que montar aquí).
//   3. Muestra un toast Quantum con el evento más reciente.
export function useEmaSmaNotifications() {
  const [toast, setToast] = useState(null)
  const dismissToast = useCallback(() => setToast(null), [])

  // candles: array completo del símbolo/TF actual (incluye la vela en
  //   formación en la última posición).
  // config: cfg YA resuelto de ema_sma_quantum (mas + notifications).
  // context: string estable que identifica "dónde" se está vigilando (p.ej.
  //   `${symbol}:${timeframe}`) — cualquier cambio se trata como reset de
  //   vigilancia (sin retroactivos), igual que activar el toggle.
  // Devuelve el nuevo objeto `notifications` a persistir, o null si no hay
  // nada que cambiar.
  const computeNotificationUpdate = useCallback((candles, config, context) => {
    const notif = config?.notifications
    if (!notif) return null

    if (!notif.enabled) {
      // Apagado: no se evalúa nada. Se limpia el cursor UNA vez (si no lo
      // estaba ya) para que la PRÓXIMA activación sea siempre "fresca" — de
      // lo contrario, reactivar con un cursor congelado de antes de apagar
      // dispararía de golpe todo lo que pasó mientras estaba OFF, que es
      // exactamente la retroactividad prohibida.
      if (notif.lastContext !== null || notif.lastProcessedTime !== null) {
        return { ...notif, lastContext: null, lastProcessedTime: null }
      }
      return null
    }

    if (!Array.isArray(candles) || candles.length < 2) return null
    const bars = buildClosedBars(candles, config.mas)
    if (bars.length === 0) return null
    const lastClosedTime = bars[bars.length - 1].time

    const isFreshStart = notif.lastContext !== context || notif.lastProcessedTime == null
    if (isFreshStart) {
      if (notif.lastContext === context && notif.lastProcessedTime === lastClosedTime) return null
      // Establece la línea base SIN evaluar cruces: el primer cruce que
      // puede notificar es el de la PRÓXIMA vela que cierre a partir de aquí.
      return { ...notif, lastContext: context, lastProcessedTime: lastClosedTime }
    }

    const watches = buildWatches(config.mas)
    const { events, lastTime } = evaluateBars(bars, watches, notif.lastProcessedTime)
    if (events.length === 0) {
      if (lastTime === notif.lastProcessedTime) return null
      return { ...notif, lastProcessedTime: lastTime }
    }

    const records = events.map(e => ({
      id: nextEventId(),
      time: e.time,
      kind: e.kind,
      direction: e.direction,
      watchKey: e.watchKey,
      message: formatEventMessage(e),
    }))

    // Dedup defensivo (bug 2026-08-12: "Precio cruza a la baja EMA 20" dos
    // veces a las 15:00). La causa raíz era una carrera de hidratación remota
    // que resucitaba el cursor `lastProcessedTime` (ver fix en
    // useIndicatorConfig.js) — pero "un cruce = un evento" debe cumplirse
    // pase lo que pase con el cursor, así que esta es la garantía de última
    // línea: un cruce (misma hora + misma dirección + mismo mensaje, que ya
    // codifica de qué media o par se trata) que YA está en el log nunca se
    // vuelve a añadir, aunque se re-evalúe. Deliberadamente NO se incluye
    // `watchKey` en la identidad: los registros persistidos antes de este
    // fix no lo traen, y de incluirlo un evento nuevo (con watchKey) nunca
    // matchearía contra uno viejo (sin watchKey) que sea el MISMO cruce.
    const eventIdentity = (r) => `${r.time}:${r.direction}:${r.message}`
    const seen = new Set(notif.events.map(eventIdentity))
    const freshRecords = []
    for (const r of records) {
      const identity = eventIdentity(r)
      if (seen.has(identity)) continue
      seen.add(identity)
      freshRecords.push(r)
    }
    if (freshRecords.length === 0) {
      return { ...notif, lastProcessedTime: lastTime }
    }
    const nextEvents = [...freshRecords].reverse().concat(notif.events).slice(0, MAX_NOTIFICATION_EVENTS)
    setToast(freshRecords[freshRecords.length - 1])
    return { ...notif, lastProcessedTime: lastTime, events: nextEvents }
  }, [])

  return { toast, dismissToast, computeNotificationUpdate }
}

// Toast de CONFIRMACIÓN al activar/pausar la vigilancia de una media (el
// toggle "ojo" de cada fila) — feedback puro del toggle, deliberadamente
// independiente de computeNotificationUpdate: no es un cruce, no entra en
// `notifications.events` ni pasa por su dedup. Solo se dispara si las
// notificaciones ya están activas (si no, no hay nada que "vigilar").
export function useEmaSmaWatchToggleToast(config) {
  const [toggleToast, setToggleToast] = useState(null)
  const dismissToggleToast = useCallback(() => setToggleToast(null), [])
  // null = todavía sin línea base (primer render de esta config): se fija
  // sin toast, igual que el cursor de cruces — nunca retroactivo, así que
  // montar el panel con medias ya activas no dispara un aluvión de toasts.
  const prevActiveRef = useRef(null)
  // Misma idea pero para el interruptor maestro (gap de diseño reportado
  // 2026-08-13: el ON/OFF de "Notificaciones activas" no daba feedback,
  // a diferencia de cada ojo individual). null = línea base sin fijar.
  const prevEnabledRef = useRef(null)

  const mas = config?.mas
  const enabled = !!config?.notifications?.enabled

  useEffect(() => {
    const list = Array.isArray(mas) ? mas : []
    const currentActive = new Map(list.map(m => [m.id, !!m.active]))

    if (prevActiveRef.current === null) {
      prevActiveRef.current = currentActive
      return
    }

    if (enabled) {
      for (const ma of list) {
        const prevWasActive = prevActiveRef.current.get(ma.id)
        // undefined = media nueva (alta), no un toggle — no aplica.
        if (prevWasActive === undefined || prevWasActive === !!ma.active) continue
        setToggleToast({
          kind: ma.active ? 'watch-activated' : 'watch-paused',
          message: `Vigilancia ${ma.active ? 'activada' : 'pausada'}: Precio × ${maLabel(ma)}`,
        })
      }
    }
    prevActiveRef.current = currentActive
  }, [mas, enabled])

  useEffect(() => {
    if (prevEnabledRef.current === null) {
      prevEnabledRef.current = enabled
      return
    }
    if (prevEnabledRef.current !== enabled) {
      setToggleToast({
        kind: enabled ? 'notifications-activated' : 'notifications-paused',
        message: enabled ? 'Notificaciones activadas' : 'Notificaciones pausadas',
      })
    }
    prevEnabledRef.current = enabled
  }, [enabled])

  return { toggleToast, dismissToggleToast }
}
